import { useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { selectSidebarCollapsed } from "@/store/ui/uiSelectors";
import { selectUserProfile } from "@/store/user/userSelectors";
import CreditsBadge from "@/components/billing/CreditsBadge";
import { NAV_ITEMS } from "@/helper/constants";
import { getInitials } from "@/helper/utilities";
import logo from "@/assets/images/logo.png";
import styles from "./Sidebar.module.css";

const Sidebar = ({ mobileOpen = false, onMobileClose }) => {
  const navigate = useNavigate();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userProfile = useSelector(selectUserProfile);

  return (
    <>
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div
          className={styles.overlay}
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={classNames(styles.sidebar, {
          [styles.collapsed]: collapsed,
          [styles.mobileOpen]: mobileOpen,
        })}
        aria-label="Main navigation"
      >
        {/* Logo */}
        <div className={styles.logoArea}>
          <button
            className={styles.logoLink}
            onClick={() => navigate("/app/dashboard")}
            aria-label="Go to dashboard"
          >
            <img
              src={logo}
              alt="Talk to Ticket logo"
              className={styles.logoImg}
            />
            {!collapsed && (
              <span className={styles.logoText}>Talk to Ticket</span>
            )}
          </button>
        </div>

        {/* Nav items */}
        <nav className={styles.nav}>
          <ul className={styles.navList} role="list">
            {NAV_ITEMS.map(({ label, to, Icon, IconActive }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    classNames(styles.navItem, { [styles.active]: isActive })
                  }
                  title={collapsed ? label : undefined}
                  onClick={() => {
                    if (onMobileClose) onMobileClose();
                  }}
                >
                  {({ isActive }) => (
                    <>
                      <span className={styles.navIcon} aria-hidden="true">
                        {isActive ? (
                          <IconActive className={styles.icon} />
                        ) : (
                          <Icon className={styles.icon} />
                        )}
                      </span>
                      {!collapsed && (
                        <span className={styles.navLabel}>{label}</span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom: CreditsBadge + User info */}
        <div className={styles.bottom}>
          {!collapsed && <CreditsBadge />}
          
          <div
            className={classNames(styles.userRow, {
              [styles.userRowCollapsed]: collapsed,
            })}
          >
            <div className={styles.avatar} aria-hidden="true">
              {getInitials(userProfile?.name)}
            </div>
            {!collapsed && (
              <div className={styles.userInfo}>
                <span className={styles.userName}>
                  {userProfile?.name || "User"}
                </span>
                <span className={styles.userEmail}>
                  {userProfile?.email || ""}
                </span>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
